import { AnchorTargetTypes } from "@rsm-hcd/javascript-core";
import Anchor from "atoms/anchors/anchor";
import SupportEmailLink from "atoms/anchors/support/support-email-link";
import SupportPhoneLink from "atoms/anchors/support/support-phone-link";
import Trans from "atoms/i18n/trans";
import ChangeIndicatorIcon from "atoms/icons/change-indicator-icon";
import Paragraph from "atoms/typography/paragraph";
import { AppNameWithHtml, Casi, NfpaWithHtml } from "constants/app-name-tm";
import { FileConstants } from "constants/file-constants";
import { XmlChangeNotationConstants } from "constants/xml-change-notation-constants";
import { siteMap } from "internal-sitemap";
import ForgotPasswordButton from "molecules/forgot-password-button/forgot-password-button";
import LogInAnchor from "molecules/log-in-anchor/log-in-anchor";
import PlansAnchor from "molecules/plans-anchor/plans-anchor";
import React from "react";
import useNetworkInformation from "utilities/contexts/network-information/use-network-information";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import FileUtils from "utilities/file-utils";
import useFeatureFlags from "utilities/hooks/use-feature-flags";
import useNfpaUrlPaths from "utilities/hooks/use-nfpa-url-paths";
import { t } from "utilities/localization-utils";
import { RouteUtils } from "utilities/route-utils";
import RemoteAccessDetailsService from "utilities/services/remote-access-details-service";
import StringUtils from "utilities/string-utils";
import SupportFaqPanel from "pages/support/support-faq-panel";
import TiaIndicatorIcon from "atoms/icons/tia-indicator-icon";
import TiaType from "models/enumerations/tia-type";
import CultureResources from "utilities/interfaces/culture-resources";

interface FAQsProps {
    limit?: number;
}

type FaqConfig = {
    questionKey: keyof CultureResources;
    answerKey: keyof CultureResources;
    values?: Record<string, string>;
    components?: Record<string, React.ReactElement>;
};

const BASE_CLASS_NAME = "c-support-page__faqs";
const CHANGE_INDICATOR_CLASS_NAME = "c-icon -change-indicator";
const ANCHOR_CLASS_NAME = "-anchor";
const ANSWER_CLASS_NAME = "-answer";
const U_LIST_CLASS_NAME = "-u-list";
const TRADEMARK_CLASS_NAME = "-trademark";

const FAQs = (props: FAQsProps) => {
    const { showVideoDemoFaq } = useFeatureFlags();
    const { globalState } = useGlobalState();
    const { get: getPdf } = RemoteAccessDetailsService.useGetQuery();
    const { isOnline } = useNetworkInformation();

    const howDoILogInButtonText = t("logIn");
    const supportHours = t("support-hours");
    const { aiDisclaimer, termsUrl, nfpaMyProfileUrl, customerSupportUrl } =
        useNfpaUrlPaths();

    const relativeProviderPath = FileUtils.getLocalizedPath(
        FileConstants.GettingStarted,
        globalState.getPreferredOrCurrentCulture()
    );
    const getPdfEnabled =
        StringUtils.hasValue(relativeProviderPath) && isOnline;
    const { data: gettingStartedGuideResponse } = getPdf(
        undefined,
        { relativeProviderPath },
        { enabled: getPdfEnabled }
    );

    const gettingStartedGuide = gettingStartedGuideResponse?.resultObject;

    const defaultComponents = {
        li: <li />,
        p: <Paragraph cssClassName={ANSWER_CLASS_NAME} />,
        sup: <sup className={TRADEMARK_CLASS_NAME} />,
        ul: <ul className={U_LIST_CLASS_NAME} />,
    };
    const appNameValue = { appName: AppNameWithHtml };
    const nfpaValue = { nfpa: NfpaWithHtml };
    const nfpaNames = { ...appNameValue, ...nfpaValue };
    const casiNameValue = { casi: Casi };
    const nfpaWithCasi = { ...nfpaNames, ...casiNameValue };

    const renderFAQs = (FAQs: FaqConfig[]) =>
        FAQs.map(({ questionKey, answerKey, values, components }) => (
            <SupportFaqPanel
                buttonText={t(questionKey)}
                key={questionKey}
                cultureKey={questionKey}
                translationValues={values}>
                <Trans
                    i18nKey={answerKey}
                    components={{ ...defaultComponents, ...components }}
                    values={values}
                />
            </SupportFaqPanel>
        ));

    const platformFAQs: FaqConfig[] = [
        {
            questionKey: "support-whatIsAppName",
            answerKey: "support-whatIsAppName_answer",
            values: nfpaNames,
        },

        {
            questionKey: "support-whatsInApp",
            answerKey: "support-whatsInApp_answer",
            values: nfpaNames,
            components: {
                a: (
                    <Anchor
                        cssClassName={ANCHOR_CLASS_NAME}
                        to={siteMap.publications.public.all}
                    />
                ),
            },
        },

        {
            questionKey: "support-changeIndicators",
            answerKey: "support-changeIndicators_answer",
            values: nfpaNames,
            components: {
                new: (
                    <ChangeIndicatorIcon
                        changes={XmlChangeNotationConstants.NEW_MATERIAL}
                        cssClassName={CHANGE_INDICATOR_CLASS_NAME}
                    />
                ),
                has: (
                    <ChangeIndicatorIcon
                        changes={XmlChangeNotationConstants.HAS_CHANGES}
                        cssClassName={CHANGE_INDICATOR_CLASS_NAME}
                    />
                ),
                del: (
                    <ChangeIndicatorIcon
                        changes={XmlChangeNotationConstants.DELETION}
                        cssClassName={CHANGE_INDICATOR_CLASS_NAME}
                    />
                ),
                tia: (
                    <TiaIndicatorIcon
                        type={TiaType.Change}
                        cssClassName={CHANGE_INDICATOR_CLASS_NAME}
                    />
                ),
            },
        },

        {
            questionKey: "support-howDoIAccess",
            answerKey: "support-howDoIAccess_answer",
            values: appNameValue,
        },

        {
            questionKey: "support-howDoILogIn",
            answerKey: "support-howDoILogIn_answer",
            values: nfpaNames,
            components: {
                login: (
                    <LogInAnchor
                        buttonText={howDoILogInButtonText.toLowerCase()}
                    />
                ),
                reset: (
                    <ForgotPasswordButton
                        cssClassName={ANCHOR_CLASS_NAME}
                        buttonText={t("resetItHere").toLowerCase()}
                    />
                ),
                plans: (
                    <PlansAnchor
                        cssClassName={ANCHOR_CLASS_NAME}
                        target={AnchorTargetTypes.Blank}
                    />
                ),
            },
        },

        {
            questionKey: "support-offline-use",
            answerKey: "support-offline-use_answer",
            values: appNameValue,
        },

        {
            questionKey: "support-howDoIAccessOffline",
            answerKey: "support-howDoIAccessOffline_answer",
            components: {
                a: (
                    <Anchor
                        cssClassName={ANCHOR_CLASS_NAME}
                        target={AnchorTargetTypes.Blank}
                        to={RouteUtils.getUrl(siteMap.home)}
                    />
                ),
            },
        },
    ];

    const deviceFAQs: FaqConfig[] = [
        {
            questionKey: "support-howMuchContent",
            answerKey: "support-howMuchContent_answer",
            values: appNameValue,
        },

        {
            questionKey: "support-howDoIDeleteContent",
            answerKey: "support-howDoIDeleteContent_answer",
            values: appNameValue,
        },

        {
            questionKey: "support-whatHappensClearCookies",
            answerKey: "support-whatHappensClearCookies_answer",
            values: appNameValue,
        },

        {
            questionKey: "support-foundInAppStore",
            answerKey: "support-foundInAppStore_answer",
            values: appNameValue,
        },

        {
            questionKey: "support-offlineLimitations",
            answerKey: "support-offlineLimitations_answer",
            values: appNameValue,
        },

        {
            questionKey: "support-browserRequirements",
            answerKey: "support-browserRequirements_answer",
            values: appNameValue,
        },
    ];

    const subscriptionAndPaymentFAQs: FaqConfig[] = [
        {
            questionKey: "support-renewSubscription",
            answerKey: "support-renewSubscription_answer",
            components: {
                a: (
                    <PlansAnchor
                        cssClassName={ANCHOR_CLASS_NAME}
                        target={AnchorTargetTypes.Blank}
                    />
                ),
            },
        },

        {
            questionKey: "support-cancelSubscription",
            answerKey: "support-cancelSubscription_answer",
            values: appNameValue,
            components: {
                a: (
                    <Anchor
                        cssClassName={ANCHOR_CLASS_NAME}
                        to={siteMap.account.subscription}
                        target={AnchorTargetTypes.Blank}
                    />
                ),
            },
        },

        {
            questionKey: "support-updateSubscription",
            answerKey: "support-updateSubscription_answer",
            values: appNameValue,
            components: {
                a: (
                    <Anchor
                        cssClassName={ANCHOR_CLASS_NAME}
                        target={AnchorTargetTypes.Blank}
                        to={RouteUtils.getUrl(siteMap.account.subscription)}
                    />
                ),
            },
        },

        {
            questionKey: "support-accountSettings",
            answerKey: "support-accountSettings_answer",
            values: nfpaValue,
            components: {
                a: (
                    <Anchor
                        cssClassName={ANCHOR_CLASS_NAME}
                        external
                        to={nfpaMyProfileUrl}
                        target={AnchorTargetTypes.Blank}
                    />
                ),
            },
        },

        {
            questionKey: "support-refundPolicy",
            answerKey: "support-refundPolicy_answer",
            values: nfpaValue,
            components: {
                a: (
                    <Anchor
                        cssClassName={ANCHOR_CLASS_NAME}
                        external
                        target={AnchorTargetTypes.Blank}
                        to={customerSupportUrl}
                    />
                ),
            },
        },

        {
            questionKey: "support-plans",
            answerKey: "support-plans_answer",
            values: appNameValue,
            components: {
                a: (
                    <PlansAnchor
                        cssClassName={ANCHOR_CLASS_NAME}
                        target={AnchorTargetTypes.Blank}
                    />
                ),
            },
        },
    ];

    const casiFAQs: FaqConfig[] = [
        {
            questionKey: "support-casi",
            answerKey: "support-casi-answer",
            values: casiNameValue,
        },
        {
            questionKey: "support-ai-use",
            answerKey: "support-ai-use-answer",
            values: nfpaWithCasi,
        },
        {
            questionKey: "support-casi-content",
            answerKey: "support-casi-content-answer",
            values: nfpaWithCasi,
        },
        {
            questionKey: "support-casi-future",
            answerKey: "support-casi-future-answer",
            values: nfpaWithCasi,
        },
    ];

    const notebooksFAQs: FaqConfig[] = [
        {
            questionKey: "support-download",
            answerKey: "support-download-answer",
        },
        {
            questionKey: "support-share-content",
            answerKey: "support-share-content-answer",
        },
        {
            questionKey: "support-notebook-page",
            answerKey: "support-notebook-page-answer",
        },
        {
            questionKey: "support-page-access",
            answerKey: "support-page-access-answer",
        },
    ];

    const generalFAQs: FaqConfig[] = [
        {
            questionKey: "support-freeTrial",
            answerKey: "support-freeTrial_answer",
            values: nfpaNames,
            components: {
                enroll: (
                    <Anchor
                        cssClassName={ANCHOR_CLASS_NAME}
                        target={AnchorTargetTypes.Blank}
                        to={RouteUtils.getUrl(siteMap.signup.freeTrial)}
                    />
                ),
                free: (
                    <PlansAnchor
                        cssClassName={ANCHOR_CLASS_NAME}
                        target={AnchorTargetTypes.Blank}
                    />
                ),
            },
        },

        {
            questionKey: "support-licenseAgreement",
            answerKey: "support-licenseAgreement_answer",
            values: appNameValue,
            components: {
                eula: (
                    <Anchor
                        cssClassName={ANCHOR_CLASS_NAME}
                        external
                        target={AnchorTargetTypes.Blank}
                        to={termsUrl}
                    />
                ),
                ai: (
                    <Anchor
                        cssClassName={ANCHOR_CLASS_NAME}
                        external
                        target={AnchorTargetTypes.Blank}
                        to={aiDisclaimer}
                    />
                ),
            },
        },

        {
            questionKey: "support-otherFormsOfContact",
            answerKey: "support-otherFormsOfContact_answer",
            values: {
                supportHours: supportHours,
            },
            components: {
                email: <SupportEmailLink />,
                phone: <SupportPhoneLink supportNumber={"UsPhoneNumber"} />,
                altPhone: (
                    <SupportPhoneLink supportNumber={"AltUsPhoneNumber"} />
                ),
                internationalPhone: (
                    <SupportPhoneLink
                        supportNumber={"InternationalPhoneNumber"}
                    />
                ),
                mexicoPhone: (
                    <SupportPhoneLink supportNumber={"MexicoPhoneNumber"} />
                ),
            },
        },
    ];

    const FAQs = [
        ...renderFAQs(platformFAQs),

        ...renderFAQs(deviceFAQs),

        ...renderFAQs(subscriptionAndPaymentFAQs),

        ...renderFAQs(casiFAQs),

        ...renderFAQs(notebooksFAQs),

        ...renderFAQs(generalFAQs),
    ];

    if (showVideoDemoFaq) {
        const videoDemoFAQ: FaqConfig = {
            questionKey: "support-videoDemos",
            answerKey: "support-videoDemos_answer",
            values: appNameValue,
            components: {
                guide: (
                    <Anchor
                        cssClassName={ANCHOR_CLASS_NAME}
                        external={true}
                        target={AnchorTargetTypes.Blank}
                        to={gettingStartedGuide?.url!}
                    />
                ),
                reference: (
                    <Anchor
                        cssClassName={ANCHOR_CLASS_NAME}
                        external={true}
                        target={AnchorTargetTypes.Blank}
                        to="https://youtu.be/1BF4LOy-DL4"
                    />
                ),
            },
        };

        FAQs.push(...renderFAQs([videoDemoFAQ]));
    }

    const limit = props.limit ?? FAQs.length;

    const FAQsToRender: React.JSX.Element[] = FAQs.slice(0, limit);

    return <div className={BASE_CLASS_NAME}>{FAQsToRender}</div>;
};

export default FAQs;
